import type {
  AppointmentBookingPage,
  CalendarEvent,
  ChatRoom,
  Enduser,
  EnduserOrder,
  File,
  Form,
  FormResponse,
  ManagedContentRecord,
  Purchase,
} from "@tellescope/types-models";
import type { StripeSubscription } from "../../enduser/hooks/useStripeSubscriptions";

/**
 * Normalized Stripe subscription data for visibility evaluation.
 * This is a simplified representation with pre-extracted fields.
 */
export interface StripeSubscriptionData {
  status: string;
  productName: string;
  /** Product metadata key-value pairs from Stripe */
  productMetadata: Record<string, string>;
}

/**
 * Context for evaluating visibility conditions.
 * Contains data from various sources needed for condition evaluation.
 *
 * Array sources (formResponses, stripeSubscriptions, appointments, etc.):
 * These are arrays because each enduser can have multiple items. Conditions
 * on these sources use "any match" semantics - a condition is true if ANY item matches.
 *
 * Singular item filter sources (form, appointment, order, etc.):
 * These are single objects representing the current item being evaluated
 * in a data block's item filter. They take precedence over array versions
 * when present (for item filtering within data blocks).
 */
export interface VisibilityContext {
  enduser: Enduser;
  formResponses?: FormResponse[];
  stripeSubscriptions?: StripeSubscriptionData[];
  // Array sources for block visibility - use "any match" semantics
  appointments?: CalendarEvent[];
  appointmentBookingPages?: AppointmentBookingPage[];
  orders?: EnduserOrder[];
  chatRooms?: ChatRoom[];
  files?: File[];
  contents?: ManagedContentRecord[];
  purchases?: Purchase[];
  // Item filter sources - single object for current item being evaluated
  // These take precedence over array versions when present
  form?: Form;
  stripeSubscription?: StripeSubscriptionData;
  appointment?: CalendarEvent;
  appointmentBookingPage?: AppointmentBookingPage;
  order?: EnduserOrder;
  chatRoom?: ChatRoom;
  file?: File;
  content?: ManagedContentRecord;
  purchase?: Purchase;
}

/**
 * Loading state for visibility context data sources.
 */
export interface VisibilityContextLoadingState {
  isLoading: boolean;
  /** Per-source loading status */
  sources: {
    enduser: boolean;
    formResponses: boolean;
    stripeSubscriptions: boolean;
    appointments: boolean;
    appointmentBookingPages: boolean;
    orders: boolean;
    chatRooms: boolean;
    files: boolean;
    contents: boolean;
    purchases: boolean;
  };
}

/**
 * Converts raw Stripe subscription data to normalized visibility data.
 */
export function normalizeStripeSubscription(subscription: StripeSubscription): StripeSubscriptionData {
  return {
    status: subscription.status,
    productName: subscription.plan?.product?.name ?? "",
    productMetadata: subscription.plan?.product?.metadata ?? {},
  };
}
